import React from 'react';
import { Vacancy } from '../types/VacancyTypes';
import { formatCurrency, timeAgo } from '../utils/formatters';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface VacancyCardProps {
  vacancy: Vacancy;
  onClick: (id: string) => void;
  darkMode?: boolean;
}

const VacancyCard: React.FC<VacancyCardProps> = ({ vacancy, onClick, darkMode }) => {
  return (
    <div
      className={`vacancy-card ${darkMode ? 'dark' : ''}`}
      onClick={() => onClick(vacancy.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick(vacancy.id)}
    >
      <div className="vacancy-card-header">
        <div className="vacancy-card-company">
          <span className="company-name">{vacancy.company_name}</span>
          {vacancy.is_verified_poster && (
            <VerifiedIcon sx={{ fontSize: 16, color: '#1976d2', marginLeft: '4px' }} />
          )}
        </div>
        <div className="vacancy-card-bounty">
          {vacancy.bounty_amount > 0
            ? formatCurrency(vacancy.bounty_amount, vacancy.bounty_currency)
            : vacancy.source === 'scraped'
              ? <span className="source-badge">
                  <OpenInNewIcon sx={{ fontSize: 13 }} /> Direct Apply
                </span>
              : formatCurrency(0, vacancy.bounty_currency)
          }
        </div>
      </div>

      <h3 className="vacancy-card-title">{vacancy.title}</h3>

      <p className="vacancy-card-description">
        {vacancy.description.length > 150
          ? vacancy.description.substring(0, 150) + '...'
          : vacancy.description}
      </p>

      {vacancy.tags && (
        <div className="vacancy-card-tags">
          {vacancy.tags.department && <span className="company-tag">{vacancy.tags.department}</span>}
          {vacancy.tags.seniority && <span className="company-tag">{vacancy.tags.seniority}</span>}
          {vacancy.tags.workStyle && <span className="company-tag">{vacancy.tags.workStyle}</span>}
        </div>
      )}

      <div className="vacancy-card-meta">
        <span className="meta-item">
          <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />
          {vacancy.is_remote ? 'Remote' : vacancy.location || 'On-site'}
        </span>
        <span className="meta-item">
          <WorkOutlineIcon sx={{ fontSize: 16 }} />
          {vacancy.employment_type}
        </span>
        <span className="meta-item">
          <PeopleOutlineIcon sx={{ fontSize: 16 }} />
          {vacancy.referral_count} referral{vacancy.referral_count !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="vacancy-card-footer">
        <span className="posted-time">{timeAgo(vacancy.created_at)}</span>
        <span className="posted-by">by {vacancy.posted_by_name}</span>
      </div>
    </div>
  );
};

export default VacancyCard;
